import { useEffect, useRef, useState } from "react";
import { StockPriceRegister, ValidationResultItem } from "@/types/types";
import { useStockPriceRegisterCreation } from "@/hooks/useStockPriceRegisterCreation";
import { ClickEvent } from "devextreme/ui/button";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import {
  Button,
  LoadIndicator,
  Popup,
  ValidationSummary,
  Validator,
  DateBox,
} from "devextreme-react";
import ServerResponse from "@/components/ServerResponse";
import { AxiosError } from "axios";
import { RequiredRule } from "devextreme-react/validator";
import { useParams } from "react-router";
import NumberBoxWrapper from "@/components/NumberBoxWrapper";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { NumberBoxRef } from "devextreme-react/cjs/number-box";
import { DateBoxRef } from "devextreme-react/cjs/date-box";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef<StockPriceRegister, string>>;
};

const validationGroupName = "insertStockPriceRegisterVal";

export default function Insert({ datagridRef, popupRef }: Props) {
  const params = useParams();

  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);
  const {
    mutate,
    data: response,
    isPending,
    isError,
    error,
    isSuccess,
  } = useStockPriceRegisterCreation(params.id as string);

  const valueRef = useRef<NumberBoxRef>(null);
  const timestampRef = useRef<DateBoxRef>(null);

  function submit(e: ClickEvent) {
    setServerErrors(null);

    if (!e.validationGroup.validate().isValid) return;

    const date = new Date(
      timestampRef?.current?.instance().option("value")?.toLocaleString() ||
        new Date().toLocaleString()
    );

    const newTimestamp = new Date(date.setDate(date.getDate() + 1));

    const requestData: Partial<StockPriceRegister> = {
      value: valueRef.current?.instance().option("value") ?? 0,
      timestamp: newTimestamp,
    };

    mutate(requestData);
  }

  function disableInputs(state: boolean) {
    valueRef.current?.instance().option("disabled", state);
    timestampRef.current?.instance().option("disabled", state);
  }

  function clearForm() {
    valueRef?.current?.instance().option("value", undefined);
    timestampRef?.current?.instance().option("value", undefined);
  }

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        clearForm();
        closePopup(popupRef);
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    disableInputs(isPending);
  }, [isPending]);

  return (
    <Popup
      title="Add Stock Price Register entry"
      showCloseButton={true}
      ref={popupRef}
      onHiding={() => {
        setServerErrors(null);
        reloadGrid(datagridRef);
      }}
    >
      <form className="mx-1 grid grid-cols-1 gap-8">
        <DateBox
          label="Date*"
          ref={timestampRef}
          labelMode="static"
          stylingMode="outlined"
          valueChangeEvent="keyup"
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Date is required"} />
          </Validator>
        </DateBox>
        <NumberBoxWrapper
          ref={valueRef}
          label="Value"
          labelMode="static"
          stylingMode="outlined"
          valueChangeEvent="keyup"
          format="$ #,##0.##"
          showSpinButtons={true}
          showClearButton={true}
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Value is required"} />
          </Validator>
        </NumberBoxWrapper>
      </form>
      <div className="absolute bottom-4 left-4">
        <div className="text-danger">
          {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
        </div>
        <ValidationSummary validationGroup={validationGroupName} id="summary"></ValidationSummary>
        {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
      </div>
      <div className="absolute bottom-4 right-4">
        <Button
          validationGroup={validationGroupName}
          useSubmitBehavior={false}
          type="default"
          height={40}
          width={isPending ? 150 : 100}
          onClick={submit}
        >
          <LoadIndicator
            id="default-btn-loading"
            className="mr-3 inline-block align-middle"
            width={25}
            height={25}
            visible={isPending}
          />
          <span className="dx-button-text">{isPending ? "Creating" : "Create"}</span>
        </Button>
      </div>
    </Popup>
  );
}
